import React, { useState, useRef } from "react";
import { InfoIcon } from "@/components/icons";
import { CirclePlusIcon } from "@/components/icons/CirclePlusIcon";
import { CircleCheckIcon } from "@/components/icons/CircleCheckIcon";
import { Product, formatPrice } from "../product-step/types";
import { CurrencyAmount } from "@/components/CurrencyAmount";
import { BookingLanguage } from "@/types/booking";
import { ProductUspsModal } from "../product-step/card/ProductUSPs";
import { trackViewItem } from "@/utils/analytics";
import { resolveProductKey } from "@/locales";
import { useTranslation } from "react-i18next";
import { languageMap } from "@/i18n";
import { useMembership } from "@/contexts/MembershipContext";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { marketHasMembership } from "@/config/market";

interface AddOnCardProps {
  product: Product;
  isSelected: boolean;
  onToggle: () => void;
  language: BookingLanguage;
  /** ⓘ dialog flavor: packages (MRI scans) get the full package dialog,
   *  add-ons keep the compact add-on one. */
  detailsUseCase?: "package_dialog_content" | "addon_dialog_content";
}

export const AddOnCard: React.FC<AddOnCardProps> = ({
  product,
  isSelected,
  onToggle,
  language,
  detailsUseCase = "addon_dialog_content",
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const [showDescription, setShowDescription] = useState(false);
  const infoClickedRef = useRef(false);
  const { isMember, getEffectivePrice } = useMembership();
  // Membership flow (path A): the user isn't a member yet but is becoming one,
  // so show member pricing on add-ons just like an existing member would see.
  // Swiss-only + feature-flagged: inert (false) for DE / flag-off.
  const { market } = useMarket();
  const { data: featureFlags } = useFeatureFlags();
  const membershipPlanActive =
    useMembershipPlanStore((s) => s.active) &&
    marketHasMembership(market) &&
    (featureFlags?.["membership_v2"] ?? true);

  // Supports both old format (translations object) and new format (translation keys)
  const displayName =
    product.translations?.name || resolveProductKey(language, product.name);
  const description =
    product.translations?.description ||
    resolveProductKey(language, product.description);
  const hasDescription = Boolean(description) && description.trim().length > 0;

  // Resolve warning translation key
  const warningText = product.warning
    ? resolveProductKey(language, product.warning)
    : undefined;

  const shortDescription = product.short_description
    ? resolveProductKey(language, product.short_description)
    : "";

  // Optional qualifier pill (e.g. "Once for life"), driven by a translation key on
  // the product so it localizes — never raw copy.
  const qualifierText = product.qualifier_tag_key
    ? t(product.qualifier_tag_key)
    : "";

  // Get the first image from the product's images array, or use placeholder
  const productImage =
    product.images && product.images.length > 0 ? product.images[0] : null;

  const handleInfoClick = (event: React.MouseEvent) => {
    console.log(`AddOnCard - Info button clicked for: ${product.id}`);
    infoClickedRef.current = true;
    event.preventDefault();
    event.stopPropagation();

    // Track GA4 view_item event when user clicks info button
    trackViewItem({
      item_id: product.id,
      item_name: displayName,
      item_category: product.category?.name || "Add-on",
      price: product.default_price
        ? product.default_price.unit_amount / 100
        : 0,
      currency: product.default_price?.currency || "CHF",
      quantity: 1,
    });

    if (hasDescription) {
      setShowDescription(true);
    }

    // Reset the flag after a short delay
    setTimeout(() => {
      infoClickedRef.current = false;
    }, 100);
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (infoClickedRef.current) {
      console.log(
        `AddOnCard - Ignoring card click due to info button click for: ${product.id}`,
      );
      return;
    }

    console.log(`AddOnCard - Card clicked for add-on: ${product.id}`);
    // Analytics tracking is handled in the store's toggleAddOn method
    onToggle();
  };

  return (
    <>
      <div
        className="group relative h-full cursor-pointer hover:z-10"
        onClick={handleCardClick}
      >
        {/* Hover shadow underlay — fades in via opacity (GPU-composited),
            stays stable while the card scales. */}
        <div
          aria-hidden
          className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out shadow-card-lift"
        />
        <div
          className={`relative h-full rounded-2xl border-[1.6px] shadow-card-sm ${
            isSelected
              ? "bg-white border-aubergine-800"
              : "bg-white border-transparent"
          }`}
        >
          <div className="p-3 flex items-center gap-3">
            {/* Product Image */}
            <div className="w-[88px] h-[88px] bg-neutral-100 rounded-[8px] flex-shrink-0 flex items-center justify-center overflow-hidden">
              {productImage ? (
                <img
                  src={productImage}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    target.parentElement!.innerHTML =
                      '<div class="w-10 h-10 bg-neutral-400 rounded opacity-60"></div>';
                  }}
                />
              ) : (
                <div className="w-10 h-10 bg-neutral-400 rounded opacity-60"></div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex-grow flex flex-col justify-center min-w-0 gap-0.5">
              {/* Title with inline pills and trailing info icon. */}
              <h3 className="text-base font-medium text-neutral-900 leading-tight">
                {(() => {
                  const words = displayName.trim().split(/\s+/);
                  const last = words.pop() ?? "";
                  const head = words.join(" ");
                  return (
                    <>
                      {head && head + " "}
                      <span className="whitespace-nowrap">
                        {last}
                        {qualifierText && (
                          <span className="ml-1 inline-flex items-center justify-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs leading-tight text-neutral-600 align-middle normal-case">
                            {qualifierText}
                          </span>
                        )}
                        {hasDescription && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={handleInfoClick}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ")
                                handleInfoClick(
                                  e as unknown as React.MouseEvent,
                                );
                            }}
                            aria-label="Show add-on details"
                            className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-neutral-100 transition-colors align-middle cursor-pointer relative -top-px"
                          >
                            <InfoIcon size={16} />
                          </span>
                        )}
                      </span>
                      {warningText && (
                        <span className="ml-1 inline-flex items-center justify-center rounded-full border border-neutral-200 px-2 min-h-[22px] py-0.5 text-center text-xs leading-tight text-neutral-600 align-middle">
                          {t("addOnsStep.gpConfirmation")}
                        </span>
                      )}
                    </>
                  );
                })()}
              </h3>

              {/* Short Description */}
              {shortDescription && (
                <p className="text-sm text-neutral-500 leading-tight line-clamp-3">
                  {shortDescription}
                </p>
              )}

              {/* Price — third line, below the description: one-off price
                  struck through directly before the bold member price. */}
              <div className="text-sm leading-tight whitespace-nowrap">
                {product.default_price ? (
                  (() => {
                    const originalAmount = product.default_price.unit_amount;
                    const memberAmount = isMember
                      ? getEffectivePrice(product)
                      : membershipPlanActive
                        ? (product.member_price?.unit_amount ?? originalAmount)
                        : originalAmount;
                    const hasDiscount =
                      (isMember || membershipPlanActive) &&
                      memberAmount < originalAmount;
                    return (
                      <div className="flex items-baseline gap-1 whitespace-nowrap">
                        <CurrencyAmount
                          market={market}
                          symbolClassName="text-sm text-neutral-600"
                        >
                          {hasDiscount ? (
                            <>
                              <span className="text-sm text-neutral-600 line-through">
                                {formatPrice(originalAmount, market, true)}
                              </span>{" "}
                              <span className="text-sm font-bold text-neutral-900">
                                {formatPrice(memberAmount, market, true)}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm font-medium text-neutral-900">
                              {formatPrice(originalAmount, market, true)}
                            </span>
                          )}
                        </CurrencyAmount>
                      </div>
                    );
                  })()
                ) : (
                  <span className="text-neutral-500">
                    {t("addOnsStep.priceNotAvailable")}
                  </span>
                )}
              </div>
            </div>

            {/* Selection Indicator */}
            <div className="flex-shrink-0">
              {isSelected ? (
                <CircleCheckIcon />
              ) : (
                <CirclePlusIcon className="text-aubergine-600" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rich add-on dialog (ProductUspsModal) */}
      <ProductUspsModal
        isOpen={showDescription}
        onClose={() => setShowDescription(false)}
        product={product}
        productId={product.id}
        productName={displayName}
        language={language}
        useCase={detailsUseCase}
        onSelectAndContinue={() => {
          if (!isSelected) onToggle();
          setShowDescription(false);
        }}
      />
    </>
  );
};
